import { useGetList, type Identifier } from "ra-core";
import { useCallback, useMemo } from "react";

import type { Sale } from "@/components/atomic-crm/types";

import { colorDeResponsable } from "../dominio/calendario";
import { iniciales } from "../negocios/formato";

/**
 * Usuarios para pintar al responsable de cada ficha (tarjeta del tablero, calendario):
 * nombre, iniciales y un color fijo por persona. Incluye los desactivados, porque sus
 * fichas antiguas siguen mostrando quién las llevaba.
 */
export const useResponsables = () => {
  const { data } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "id", order: "ASC" },
    },
    { staleTime: 5 * 60 * 1000 },
  );
  const usuarios = useMemo(() => data ?? [], [data]);
  const ids = useMemo(() => usuarios.map((u) => u.id), [usuarios]);

  const nombre = useCallback(
    (id?: Identifier | null) => {
      const u = usuarios.find((x) => String(x.id) === String(id));
      return u ? `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() : "";
    },
    [usuarios],
  );
  const color = useCallback(
    (id?: Identifier | null) => colorDeResponsable(id ?? null, ids),
    [ids],
  );
  const inicial = useCallback(
    (id?: Identifier | null) => iniciales(nombre(id)).slice(0, 1) || "·",
    [nombre],
  );
  /** Usuarios activos, para la leyenda de colores. */
  const activos = useMemo(
    () => usuarios.filter((u) => !u.disabled),
    [usuarios],
  );

  /** 30: ruta de la foto (null sin foto o sin la 30). */
  const foto = useCallback(
    (id?: Identifier | null) =>
      (
        usuarios.find((x) => String(x.id) === String(id)) as
          | { foto?: string | null }
          | undefined
      )?.foto ?? null,
    [usuarios],
  );

  return { usuarios, activos, nombre, color, inicial, foto };
};
